import { useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  notFound,
  useNavigate,
} from "@tanstack/react-router";
import { useState } from "react";

import { useDeleteEvent } from "../features/events/api/mutations";
import { eventQueries } from "../features/events/api/queries";
import { AttendanceToggle } from "../features/events/components/AttendanceToggle";
import { isInheriting } from "../features/attachments/inheritance";
import { ImagesEditor } from "../features/images/components/ImagesEditor";
import { LinksEditor } from "../features/links/components/LinksEditor";
import { describeRepeat, parseRepeat } from "../features/series/rrule";
import { periodFor } from "../features/weather/phrasing";
import { OutlookChip } from "../features/weather/components/OutlookChip";
import { AppHeader } from "../shared/ui/AppHeader";

export const Route = createFileRoute("/_authenticated/events/$eventId/")({
  loader: async ({ params, context: { queryClient } }) => {
    const data = await queryClient.ensureQueryData(
      eventQueries.detail(params.eventId),
    );
    // Fail in the loader, so the component never renders a missing event.
    if (!data.event) throw notFound();
  },
  component: EventDetailPage,
  notFoundComponent: () => (
    <>
      <AppHeader />
      <main className="mx-auto max-w-2xl p-6">
        <h1 className="text-xl font-semibold">Event not found</h1>
        <p className="mt-2 text-sm text-content-muted">
          It may have been removed.
        </p>
        <Link
          to="/events"
          className="mt-4 inline-block text-sm text-brand underline"
        >
          Back to events
        </Link>
      </main>
    </>
  ),
});

const dateTime = new Intl.DateTimeFormat("en-GB", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function EventDetailPage() {
  const { eventId } = Route.useParams();
  const navigate = useNavigate();
  const { data } = useSuspenseQuery(eventQueries.detail(eventId));
  const deleteEvent = useDeleteEvent();
  const [confirming, setConfirming] = useState(false);

  const event = data.event;
  // The loader guarantees this, but the type does not.
  if (!event) return null;

  const price =
    event.price === null
      ? "Unknown"
      : event.price.minorAmount === 0
        ? "Free"
        : new Intl.NumberFormat("en-GB", {
            style: "currency",
            currency: event.price.currency,
            maximumFractionDigits: 0,
          }).format(event.price.minorAmount / 100);

  const handleDelete = async () => {
    await deleteEvent.mutateAsync(event.id);
    await navigate({ to: "/events" });
  };

  /*
   * Whether what is shown belongs to this night or to the whole run.
   *
   * Derived rather than read from a flag — the server exposes none, deliberately,
   * because it is derivable and a flag would be a second source of truth. Worked
   * out **per type**: an occurrence can carry its own poster while still
   * inheriting the run's links, which is what "resolved per type" means.
   */
  const imagesInherited = isInheriting(event.images, event.series?.images);
  const linksInherited = isInheriting(event.links, event.series?.links);

  return (
    <>
      <AppHeader />
      <main className="mx-auto max-w-2xl p-6">
        <Link
          to="/events"
          className="text-sm text-content-muted hover:text-content"
        >
          ← All events
        </Link>

        <div className="mt-4 flex items-start justify-between gap-4">
          <h1
            className={
              event.isCancelled
                ? "text-2xl font-semibold line-through decoration-2 text-content-muted"
                : "text-2xl font-semibold"
            }
          >
            {event.title}
          </h1>
          {event.isCancelled && (
            <span className="shrink-0 rounded-full bg-brand px-2.5 py-1 text-xs font-medium text-brand-contrast">
              Cancelled
            </span>
          )}
        </div>

        {event.description && (
          <p className="mt-3 whitespace-pre-line text-content-muted">
            {event.description}
          </p>
        )}

        {/*
          Primary first, then the rest in the server's order.

          `alt` is always rendered — empty when we have none, which tells a
          screen reader to skip a decorative image rather than read out a URL.
          width/height reserve space so the page does not jump on load.
        */}
        {event.images.length > 0 && (
          <div className="mt-4 flex flex-col gap-2">
            <div className="grid gap-2 sm:grid-cols-2">
              {[...event.images]
                .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary))
                .map((image) => (
                  <figure key={image.id} className="flex flex-col gap-1">
                    <img
                      src={image.url}
                      alt={image.altText ?? ""}
                      width={image.width ?? undefined}
                      height={image.height ?? undefined}
                      className="w-full rounded-lg object-cover"
                    />
                    {image.credit && (
                      <figcaption className="text-xs text-content-muted">
                        {image.credit}
                      </figcaption>
                    )}
                  </figure>
                ))}
            </div>
            {imagesInherited && (
              <p className="text-xs text-content-muted">
                From the series — every night in this run shows these.
              </p>
            )}
          </div>
        )}

        {/* Above the details, with counts: this is the primary action on the page,
            and burying it under the definition list would hide it. */}
        <div className="mt-4">
          <AttendanceToggle event={event} detailed />
        </div>

        <dl className="mt-6 grid gap-x-6 gap-y-3 sm:grid-cols-[8rem_1fr]">
          <dt className="text-sm font-medium text-content-muted">Starts</dt>
          <dd>
            <time dateTime={event.startsAt}>
              {dateTime.format(new Date(event.startsAt))}
            </time>
          </dd>

          {event.endsAt && (
            <>
              <dt className="text-sm font-medium text-content-muted">Ends</dt>
              <dd>
                <time dateTime={event.endsAt}>
                  {dateTime.format(new Date(event.endsAt))}
                </time>
              </dd>
            </>
          )}

          {event.organisers.length > 0 && (
            <>
              <dt className="text-sm font-medium text-content-muted">
                Put on by
              </dt>
              <dd className="flex flex-col gap-0.5">
                {event.organisers.map((involvement) => (
                  <span key={involvement.organiser.id}>
                    {involvement.organiser.name}
                    {/* Free text, so it is rendered as written rather than
                        mapped through a vocabulary that does not exist. */}
                    {involvement.role && (
                      <span className="text-sm text-content-muted">
                        {" — "}
                        {involvement.role}
                      </span>
                    )}
                  </span>
                ))}
              </dd>
            </>
          )}

          {event.venue && (
            <>
              <dt className="text-sm font-medium text-content-muted">Where</dt>
              <dd>
                {event.venue.name}
                {event.venue.isOutdoor && (
                  <span className="ml-2 rounded-full bg-surface-muted px-2 py-0.5 text-xs text-content-muted">
                    Outdoor
                  </span>
                )}
                {(event.venue.neighbourhood ?? event.venue.addressLine) && (
                  <span className="block text-sm text-content-muted">
                    {[event.venue.neighbourhood, event.venue.addressLine]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                )}
              </dd>
            </>
          )}

          {event.outlook && (
            <>
              <dt className="text-sm font-medium text-content-muted">
                {/* The label itself distinguishes them — a climate average is not
                    "the weather", it is what this time of year is like. */}
                {event.outlook.kind === "FORECAST"
                  ? "Weather"
                  : "Typical weather"}
              </dt>
              <dd>
                <OutlookChip outlook={event.outlook} detailed />

                {/*
                 * The forecast band spelled out, which the chip only hints at
                 * with a "less certain" qualifier. Shown here because the detail
                 * page is where someone decides, and the p10–p90 range is the
                 * honest answer to "how sure is this?".
                 */}
                {event.outlook.forecast &&
                  event.outlook.forecast.tempP10C !== null &&
                  event.outlook.forecast.tempP90C !== null && (
                    <span className="mt-1 block text-sm text-content-muted">
                      Likely between{" "}
                      {Math.round(event.outlook.forecast.tempP10C)}° and{" "}
                      {Math.round(event.outlook.forecast.tempP90C)}°
                      {event.venue?.area && ` in ${event.venue.area.name}`}.
                    </span>
                  )}

                {/*
                 * States the basis in full rather than leaving it in a tooltip:
                 * this is a page someone reads, and "not a forecast" is the one
                 * thing they must not miss.
                 */}
                {event.outlook.climate && (
                  <span className="mt-1 block text-sm text-content-muted">
                    Based on the last {event.outlook.climate.sampleYears} years
                    for {periodFor(event.outlook.climate.monthDay)} (±7 days)
                    {event.venue?.area && ` in ${event.venue.area.name}`} — this
                    is a long-term average, not a forecast.
                  </span>
                )}
              </dd>
            </>
          )}

          {event.series && (
            <>
              <dt className="text-sm font-medium text-content-muted">
                Repeats
              </dt>
              <dd>
                {describeRepeat(
                  parseRepeat(event.series.rrule),
                  new Date(event.startsAt),
                  event.series.until ? new Date(event.series.until) : null,
                )}
                {event.isException && (
                  <span className="mt-1 block text-sm text-content-muted">
                    This date has been edited on its own, so it no longer
                    follows the series.
                  </span>
                )}
                {/*
                  The way to the series itself — where a poster for the whole
                  run is attached, rather than to this night. Owner-only,
                  matching the page's other affordances; the server checks it
                  again on every write.
                */}
                {event.series.isMine && !event.series.isCancelled && (
                  <Link
                    to="/series/$seriesId/edit"
                    params={{ seriesId: event.series.id }}
                    className="mt-1 block text-sm text-brand underline"
                  >
                    Edit the whole run
                  </Link>
                )}
              </dd>
            </>
          )}

          <dt className="text-sm font-medium text-content-muted">Price</dt>
          <dd>{price}</dd>

          {event.tags.length > 0 && (
            <>
              <dt className="text-sm font-medium text-content-muted">Tags</dt>
              <dd className="flex flex-wrap gap-2">
                {event.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="rounded-full bg-surface-muted px-2 py-0.5 text-xs text-content-muted"
                  >
                    {tag.label}
                  </span>
                ))}
              </dd>
            </>
          )}

          {event.ticketUrl && (
            <>
              <dt className="text-sm font-medium text-content-muted">
                Tickets
              </dt>
              <dd>
                <a
                  href={event.ticketUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-brand underline"
                >
                  {event.ticketUrl}
                </a>
              </dd>
            </>
          )}

          {/*
            Separate from Tickets above, deliberately: `ticketUrl` stays the
            canonical ticket link so "where do I buy" is not a list scan, and
            these are everything else — the Facebook event, the set times.

            Shown to everyone; only the owner gets the editor further down.
          */}
          {event.links.length > 0 && (
            <>
              <dt className="text-sm font-medium text-content-muted">
                Links
                {linksInherited && (
                  <span className="ml-2 font-normal">(from the series)</span>
                )}
              </dt>
              <dd className="flex flex-col gap-1">
                {event.links.map((link) => (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="break-all text-brand underline"
                  >
                    {link.label ?? link.url}
                  </a>
                ))}
              </dd>
            </>
          )}
        </dl>

        {/*
          The link editor, for the owner only.

          On the detail page rather than the edit form because links save
          immediately — they are rows with their own ids, not fields of the
          event, so folding them into the edit form's submit would mean holding
          unsaved rows in state for no gain.

          `isMine` drives the affordance; the real check is server-side, where
          `addEventLink` requires the event's `createdById`.
        */}
        {event.isMine && !event.isCancelled && (
          <div className="mt-8 flex flex-col gap-8 border-t border-border-subtle pt-6">
            <LinksEditor
              owner="event"
              ownerId={event.id}
              /*
               * The occurrence's OWN links, not the resolved list. Handing the
               * inherited ones to the editor would put a Remove button on rows
               * belonging to the series — and pressing it would strip them from
               * every night in the run.
               */
              links={linksInherited ? [] : event.links}
              heading={
                linksInherited
                  ? "Links for this night (currently the series’)"
                  : "Links for this event"
              }
            />

            <ImagesEditor
              owner="event"
              ownerId={event.id}
              images={imagesInherited ? [] : event.images}
              heading="Images for this event"
              /*
               * Only when actually inheriting. `showOverride` gives the editor
               * its explanation and the button that starts an override — adding
               * the first image is what performs it, so there is nothing to
               * "turn on" beyond scrolling to the add form.
               */
              inheritedFrom={
                imagesInherited && event.series
                  ? { label: event.series.title }
                  : null
              }
              /*
               * Reverting is removing this night's own rows — which is exactly
               * `Remove` on each, so the editor needs no separate revert path.
               * The hint says so, because "delete to get them back" is not
               * obvious.
               */
              revertHint={
                !imagesInherited &&
                event.series &&
                event.series.images.length > 0
                  ? `Remove these to go back to ${event.series.title}’s images.`
                  : null
              }
            />
          </div>
        )}

        {/* Server-computed isMine drives the affordance; the real check is in
            the service, which rejects a mutation from a non-owner. */}
        {event.isMine && !event.isCancelled && (
          <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-border-subtle pt-6">
            <Link
              to="/events/$eventId/edit"
              params={{ eventId: event.id }}
              className="rounded-md border border-border-subtle px-4 py-2 hover:bg-surface-muted"
            >
              Edit
            </Link>

            {confirming ? (
              <>
                <span className="text-sm text-content-muted">
                  Cancel this event?
                </span>
                <button
                  type="button"
                  onClick={() => void handleDelete()}
                  disabled={deleteEvent.isPending}
                  className="rounded-md bg-brand px-4 py-2 font-medium text-brand-contrast hover:bg-brand-hover disabled:opacity-60"
                >
                  {deleteEvent.isPending ? "Cancelling…" : "Yes, cancel it"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirming(false);
                  }}
                  className="rounded-md border border-border-subtle px-4 py-2 hover:bg-surface-muted"
                >
                  Keep it
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setConfirming(true);
                }}
                className="rounded-md border border-border-subtle px-4 py-2 text-brand hover:bg-surface-muted"
              >
                Cancel event
              </button>
            )}
          </div>
        )}
      </main>
    </>
  );
}
